import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { jobService } from "../services/job.service";
import { gridClasses, primaryBtnModernClasses, secondaryBtnModernClasses } from "../styles/uiClasses";
import { useAsyncData } from "../hooks/useAsyncData";

import { SkeletonGrid } from "../components/SkeletonGrid";
import { EmptyState } from "../components/EmptyState";
import { JobCard } from "../components/JobCard";
import { IJobView } from "../interfaces/job/iJobView.interface";
import { JobQueryVM } from "../interfaces/job/iJobQuery.interface";
import { PageContainer } from "../components/PageContainer";
import { JobSortingEnum } from "../enums/jobSorting.enum";
import { PageSize } from "../const/page.const";
import {
    heroPanelClasses,
    pageHeadingLargeClasses,
    pageLeadClasses,
    subtleEyebrowClasses,
} from "../styles/pageClasses";
import { homePageClasses } from "../styles/pageSpecificClasses";
import "../styles/heroBackgrounds.css";

type JobListResponse = {
    items: IJobView[];
    totalCount: number;
};

export default function Home() {
    const [searchParams] = useSearchParams();
    const [debouncedQ, setDebouncedQ] = useState("");
    const [page, setPage] = useState(1);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    const pageSize = PageSize;
    const q = searchParams.get("q") ?? "";
    const sort = JobSortingEnum.New;

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedQ(q);
        }, 220);

        return () => window.clearTimeout(timer);
    }, [q]);

    const { data: jobListData, loading, error } = useAsyncData<JobListResponse>(
        () => {
            const query: JobQueryVM = { q: debouncedQ.trim() || undefined, sort, page, pageSize };
            // Public read-only mode for now.
            // Previous auth-aware version:
            // return token
            //     ? jobService.getAllAuthorized(request, query)
            //     : jobService.getAll(query);
            return jobService.getAll(query);
        },
        [debouncedQ, sort, page]
    );

    const data = jobListData?.items ?? [];
    const totalCount = jobListData?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const showInitialLoading = !hasLoadedOnce && loading;

    useEffect(() => {
        if (!loading && jobListData) {
            setHasLoadedOnce(true);
        }
    }, [loading, jobListData]);

    useEffect(() => {
        setPage(1);
    }, [debouncedQ]);

    const goPrev = () => setPage((p) => Math.max(1, p - 1));
    const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

    return (
        <PageContainer maxWidth="7xl" contentClassName={homePageClasses.pageSpacing}>
            <section className={`${heroPanelClasses} ${homePageClasses.heroSection}`}>
                <div className={homePageClasses.heroGrid}>
                    <div className={homePageClasses.heroCopyCard}>
                        <div>
                            <span className={subtleEyebrowClasses}>Summer Hiring Platform</span>
                            <h1 className={`mt-4 max-w-3xl ${pageHeadingLargeClasses}`}>
                                Seasonal jobs across Greece, styled with a little sun, sea, and structure.
                            </h1>
                            <p className={`max-w-2xl ${pageLeadClasses}`}>
                                Browse hospitality, tourism, and retail roles from island hotspots to city centers, with a calmer experience that feels closer to a travel guide than a dashboard.
                            </p>
                        </div>

                        <div className={homePageClasses.statsGrid}>
                            <div className={homePageClasses.statCard}>
                                <p className={homePageClasses.statLabelMuted}>Active listings</p>
                                <p className={homePageClasses.statValue}>{totalCount}</p>
                            </div>
                            <div className={homePageClasses.statCardAlt}>
                                <p className={homePageClasses.statLabel}>Search status</p>
                                <p className={homePageClasses.statStatus}>
                                    {q ? `Filtered by "${q}"` : "Showing all opportunities"}
                                </p>
                            </div>
                        </div>

                        <div className={homePageClasses.mobileCtaWrap}>
                            <a
                                href="#jobs-section"
                                className={`${homePageClasses.mobileCta} ${primaryBtnModernClasses}`}
                            >
                                Explore Jobs
                            </a>
                        </div>
                    </div>

                    <div className={homePageClasses.desktopPanel}>
                        <div className={homePageClasses.heroImage} />
                        <div className={homePageClasses.featureGrid}>
                            <div className={homePageClasses.featureCard}>
                                <p className={homePageClasses.featureEyebrowWarm}>Island Focus</p>
                                <p className={homePageClasses.featureCopy}>Warm-weather roles for the peak season</p>
                            </div>
                            <div className={homePageClasses.featureCardAlt}>
                                <p className={homePageClasses.featureEyebrowCool}>Fast Search</p>
                                <p className={homePageClasses.featureCopy}>Clear filters, cleaner cards, less visual noise</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div id="jobs-section" className={homePageClasses.jobsAnchor}>
                <section className={homePageClasses.postsSection}>
                    <div className={homePageClasses.sectionHeader}>
                        <div>
                            <h2 className={homePageClasses.sectionTitle}>Available Summer Jobs</h2>
                            <p className={homePageClasses.sectionCopy}>
                                Curated listings for candidates looking to move quickly.
                            </p>
                        </div>
                        <p className={homePageClasses.pageCount}>
                            Page {page} of {totalPages}
                        </p>
                    </div>

                    {error && (
                        <div className={homePageClasses.error}>
                            {error}
                        </div>
                    )}

                    {showInitialLoading ? (
                        <SkeletonGrid />
                    ) : data.length === 0 ? (
                        <EmptyState
                            title="No jobs match your search"
                            subtitle="Try adjusting your search or sorting to discover more opportunities."
                        />
                    ) : (
                        <div>
                            <div
                                className={`${gridClasses} ${homePageClasses.cardGrid}`}
                            >
                                {data.map((job) => (
                                    <JobCard
                                        key={job.id}
                                        isSaved={job.isSaved}
                                        job={job}
                                        to={`/job/${job.id}`}
                                        primaryLabel="View Details"
                                    />
                                ))}
                            </div>

                            <div className={homePageClasses.paginationWrap}>
                                {totalPages > 1 && (
                                    <div className={homePageClasses.pagination}>
                                        <button
                                            onClick={goPrev}
                                            disabled={page === 1}
                                            className={`${secondaryBtnModernClasses} ${homePageClasses.disabledButton}`}
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={goNext}
                                            disabled={page === totalPages}
                                            className={`${primaryBtnModernClasses} ${homePageClasses.disabledButton}`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </PageContainer>
    );
}
